
import React, { useEffect, useState } from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import serviceData from '../data/serviceData.json';
import Lightbox from '../components/Lightbox';

const BASE_URL = 'https://www.erainterio.com';

const BUSINESS_DETAILS = {
  telephone: '+91-8830219839',
  address: {
    '@type': 'PostalAddress',
    streetAddress:
      'Shop No.2, 1st Floor, Shyama Apartment, Pratap Nagar Square, Pratap Nagar',
    addressLocality: 'Nagpur',
    addressRegion: 'Maharashtra',
    postalCode: '440022',
    addressCountry: 'IN'
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 21.1130412,
    longitude: 79.0561782
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: [
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
        'Sunday'
      ],
      opens: '10:00',
      closes: '19:00'
    }
  ],
  sameAs: [
    'https://www.instagram.com/erainterio9/',
    'http://www.facebook.com/erainterior9',
    'https://www.youtube.com/@eraintero'
  ]
};

const seoData = {
  1: {
    title: 'Residential Interior Design Services for Stylish Homes',
    description:
      'Trusted residential interior design services for apartments, flats and villas. See our work and plan your dream home today.',
    canonical: `${BASE_URL}/services/residential-interior-design`,
    ogTitle: 'Residential Interior Design Services That Feel Like You',
    ogDescription:
      'Looking for a home decor shop in Nagpur? Get stylish interiors and decor ideas for every room, all in one place.',
    ogImage: `${BASE_URL}/images/Residential.jpeg`,
    keywords:
      'Residential Interior Design Services, Residential Interior Design, 3 BHK Flat Interior Design, Home Decor Shop in Nagpur, Home Decor Nagpur, Office Furniture Nagpur',
    schemaName: 'Residential Interior Design Services',
    h1: 'Residential Interior Design Services for Stylish Homes',
    imageAlt:
      'Residential interior design services for stylish homes in Nagpur'
  },

  2: {
    title: 'Luxury Home Interior Designer in Nagpur for Homes & Offices',
    description:
      'Planning a new space? Our luxury home interior designer in Nagpur designs elegant homes, offices and showrooms, from concept to handover.',
    canonical: `${BASE_URL}/services/Furniture-And-Home-Decor`,
    ogTitle:
      'Stylish Spaces by a Luxury Home Interior Designer in Nagpur',
    ogDescription:
      'Your ideas, your style, your space. See how a luxury home interior designer in Nagpur turns homes and offices into something special.',
    ogImage: `${BASE_URL}/images/furniture&decor.jpg`,
    keywords:
      'Luxury Home Interior Designer in Nagpur, Office Furniture in Nagpur, Home Decor Nagpur, Sofa Set, Modern Kitchen Design',
    schemaName: 'Luxury Home Interior Designer in Nagpur',
    h1: 'Your Dream Space, Designed by a Luxury Home Interior Designer in Nagpur',
    imageAlt:
      'Luxury home interior design, furniture and home decor in Nagpur'
  },

  3: {
    title: 'Commercial Interior Designer in Nagpur for Shops & Showrooms',
    description:
      'Planning a new office or store? Work with a Nagpur interior designer who plans the layout, builds it and hands over a ready space.',
    canonical: `${BASE_URL}/services/Commercial-Interior-Design`,
    ogTitle:
      'Smart Offices by a Commercial Interior Decorator Nagpur',
    ogDescription:
      'Offices, clinics, cafes and showrooms: see how commercial interior designers plan layout, lighting and furniture around your brand.',
    ogImage: `${BASE_URL}/images/commercial.jpg`,
    keywords:
      'Commercial Interior Designer in Nagpur, Commercial Interior Decorator Nagpur, Commercial Interior Designers, Nagpur Interior Designer, Nagpur Interior',
    schemaName: 'Commercial Interior Designer in Nagpur',
    h1: 'Commercial Interior Designer in Nagpur for Shops & Showrooms',
    imageAlt:
      'Commercial interior designer in Nagpur for offices, shops and showrooms'
  },

  4: {
    title: 'Home Interior Lighting for Bedrooms, Living Rooms & More',
    description:
      'Home interior lighting can change how every room looks and feels. Explore ideas for bedrooms, living rooms and kitchens, planned around you.',
    canonical: `${BASE_URL}/services/Interior-Lighting-Design`,
    ogTitle: 'Make Your Home Shine with Smart Home Interior Lighting',
    ogDescription:
      'Dreaming of a cozier home? Explore ceiling lighting and bedroom lighting ideas that set the perfect mood in every room.',
    ogImage: `${BASE_URL}/images/lightingdesign.jpg`,
    keywords:
      'Home Interior Lighting, Home Design Lighting, Lighting Room Design, Interior Lighting Design, Ceiling Lighting, Bedroom Lighting',
    schemaName: 'Home Interior Lighting',
    h1: 'Home Interior Lighting That Makes Every Room Glow',
    imageAlt:
      'Home interior lighting, ceiling lighting and bedroom lighting ideas'
  },

  5: {
    title: 'Home Interior Colour | Transform Your Home with Style',
    description:
      'Give your home a fresh new look with beautiful Home Interior Colour ideas, stylish palettes and elegant shades for every room.',
    canonical: `${BASE_URL}/services/home-interior-colour`,
    ogTitle:
      'Home Interior Colour Ideas | Transform Your Home with ERA Interio',
    ogDescription:
      'Explore home interior colour ideas for beautiful, balanced spaces. Discover stylish colour combinations designed to transform your home with ERA Interio.',
    ogImage: `${BASE_URL}/images/service-entry-img.jpg`,
    keywords:
      'Home Interior Colour, Room Color Combination, Color Design, Bedroom Colors, Home Color Combinations, Paint for Living Room',
    schemaName: 'Home Interior Colour',
    h1: 'Home Interior Colour Consultation',
    imageAlt:
      'Home interior colour ideas, bedroom colours and living room paint combinations'
  },

  6: {
    title: 'Best 3D Home Interior Designer | Visualize Your Dream Home',
    description:
      'Visualize your dream home in 3D with stylish designs, smart layouts, and realistic interiors tailored to your space.',
    canonical: `${BASE_URL}/services/Best-3D-Home-Interior-designer`,
    ogTitle:
      'Best 3D Home Interior Designer | Visualize Your Dream Home',
    ogDescription:
      'Discover your dream home in 3D with expert interior design, realistic visualizations, smart layouts, and personalized designs by ERA Interio.',
    ogImage: `${BASE_URL}/images/3dvisualization.jpg`,
    keywords:
      'Best 3D Home Interior Designer, 3D Interior Design, 3D Home Design Nagpur, 3D Floor Plan, 3D Room Design, 3D Visualization',
    schemaName: 'Best 3D Home Interior Designer',
    h1: 'Best 3D Home Interior Designer',
    imageAlt:
      '3D home interior design, floor plan and realistic room visualization'
  }
};

const ServiceDetail = () => {
  const { slug } = useParams();

  const serviceSlugs = {
    'residential-interior-design': 1,
    'Furniture-And-Home-Decor': 2,
    'Commercial-Interior-Design': 3,
    'Interior-Lighting-Design': 4,
    'home-interior-colour': 5,
    'Best-3D-Home-Interior-designer': 6
  };

  const serviceId = serviceSlugs[slug];
  const service = serviceData.find((item) => item.id === serviceId);
  const currentSeo = service ? seoData[service.id] : null;

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [startIndex, setStartIndex] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return <Navigate to="/page-not-found" />;
  }

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: currentSeo.schemaName,
    description: currentSeo.description,
    image: currentSeo.ogImage,
    '@id': `${currentSeo.canonical}#professionalservice`,
    url: currentSeo.canonical,
    telephone: BUSINESS_DETAILS.telephone,
    address: BUSINESS_DETAILS.address,
    geo: BUSINESS_DETAILS.geo,
    openingHoursSpecification:
      BUSINESS_DETAILS.openingHoursSpecification,
    sameAs: BUSINESS_DETAILS.sameAs
  };

  const detailedInfo = service.process || [
    'Consultation & Needs Assessment: We start by understanding your vision, lifestyle, and specific needs.',
    'Concept Development: Our team creates mood boards and layout plans to ensure everything aligns with your goals.',
    'Execution & Installation: We manage the entire process, sourcing premium materials and handling the installation.',
    'Final Reveal: You walk into a beautifully transformed space that exceeds your expectations.'
  ];

  const bgImage = service.image.startsWith('/')
    ? service.image
    : `/${service.image}`;

  const galleryImages = [
    bgImage,
    '/images/gallery-1.jpg',
    '/images/gallery-2.jpg',
    '/images/gallery-3.jpg',
    '/images/gallery-4.jpg',
    '/images/gallery-5.jpg',
    '/images/gallery-6.jpg',
    '/images/project-1.jpg',
    '/images/project-3.jpg'
  ];

  const openLightbox = (index) => {
    setStartIndex(index);
    setLightboxOpen(true);
  };

  return (
    <>
      <Helmet>
        <title>{currentSeo.title}</title>

        <meta
          name="description"
          content={currentSeo.description}
        />

        <meta
          name="keywords"
          content={currentSeo.keywords}
        />

        <meta
          name="robots"
          content="index, follow, max-image-preview:large"
        />

        <link
          rel="canonical"
          href={currentSeo.canonical}
        />

        <meta
          property="og:title"
          content={currentSeo.ogTitle}
        />

        <meta
          property="og:description"
          content={currentSeo.ogDescription}
        />

        <meta
          property="og:image"
          content={currentSeo.ogImage}
        />

        <meta
          property="og:url"
          content={currentSeo.canonical}
        />

        <meta
          property="og:type"
          content="website"
        />

        <meta
          property="og:site_name"
          content="ERA Interio"
        />

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={currentSeo.ogTitle}
        />

        <meta
          name="twitter:description"
          content={currentSeo.ogDescription}
        />

        <meta
          name="twitter:image"
          content={currentSeo.ogImage}
        />

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <style>{`
        .process-card {
          transition: all 0.4s ease;
          border-radius: 20px;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.05);
        }

        .process-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1) !important;
          border-color: transparent;
        }

        .process-icon {
          transition: all 0.4s ease;
        }

        .process-card:hover .process-icon {
          background-color: #95bad6 !important;
          transform: scale(1.1);
        }

        .premium-cta {
          background: linear-gradient(135deg, #18191b 0%, #292b2c 100%);
          color: #fff;
          position: relative;
          overflow: hidden;
        }

        .premium-cta::before {
          content: '';
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 60%);
          transform: rotate(30deg);
          pointer-events: none;
        }

        .premium-btn {
          background: #fff;
          color: #1c1c1c;
          font-weight: 600;
          border: none;
          transition: all 0.3s;
        }

        .premium-btn:hover {
          background: #95bad6;
          color: #fff;
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(149, 186, 214, 0.4);
        }

        .gallery-img-wrap {
          overflow: hidden;
          border-radius: 15px;
        }

        .gallery-img-wrap img {
          transition: transform 0.6s ease;
        }

        .photo-gallery:hover .gallery-img-wrap img {
          transform: scale(1.05);
        }

        .overview-pill {
          display: inline-block;
          padding: 6px 16px;
          background-color: rgba(149, 186, 214, 0.15);
          color: #95bad6;
          border-radius: 30px;
          font-weight: 700;
          letter-spacing: 2px;
          font-size: 0.85rem;
          margin-bottom: 20px;
        }
      `}</style>

      {/* Service Header */}
      <div
        className="page-header parallaxie"
        style={{
          backgroundImage: `url('${bgImage}')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          position: 'relative',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.65)'
          }}
        />

        <div
          className="container"
          style={{
            position: 'relative',
            zIndex: 1,
            marginTop: '80px'
          }}
        >
          <div className="row">
            <div className="col-lg-8 mx-auto text-center">
              <div className="page-header-box">
                <h1
                  className="text-anime-style-2 text-white display-3 fw-bold mb-4"
                  data-cursor="-opaque"
                >
                  {currentSeo.h1}
                </h1>

                <nav className="wow fadeInUp d-flex justify-content-center">
                  <ol className="breadcrumb bg-transparent p-0 m-0">
                    <li className="breadcrumb-item">
                      <Link
                        to="/"
                        className="text-white text-decoration-none opacity-75 hover-opacity-100"
                      >
                        Home
                      </Link>
                    </li>

                    <li className="breadcrumb-item">
                      <Link
                        to="/services"
                        className="text-white text-decoration-none opacity-75 hover-opacity-100"
                      >
                        Services
                      </Link>
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Content */}
      <div className="service-detail-section py-5">
        <div className="container mt-5 mb-3">
          <div className="row align-items-center mb-5 pb-5">
            <div className="col-lg-6 mb-5 mb-lg-0">
              <div className="service-detail-image wow fadeInLeft">
                <figure
                  className="image-anime rounded overflow-hidden shadow-lg p-2 bg-white"
                  style={{ borderRadius: '24px' }}
                >
                  <img
                    src={bgImage}
                    alt={currentSeo.imageAlt}
                    className="img-fluid w-100"
                    style={{ borderRadius: '18px' }}
                  />
                </figure>
              </div>
            </div>

            <div className="col-lg-6 px-lg-5">
              <div className="service-detail-content wow fadeInRight">
                <span className="text-uppercase overview-pill">
                  Overview
                </span>

                <h2 className="mb-4 text-anime-style-2 display-5 fw-bold text-dark">
                  {service.title}
                </h2>

                <p
                  className="lead fw-normal text-secondary mb-4"
                  style={{ fontSize: '1.25rem', lineHeight: '1.7' }}
                >
                  {service.info}
                </p>

                <p
                  className="text-muted mt-3"
                  style={{ lineHeight: '1.8', fontSize: '1.05rem' }}
                >
                  {service.description ||
                    'Our team of professionals brings extensive experience and an eye for aesthetics to deliver top-notch results tailored to your specific requirements. We believe that every project is an opportunity to create something beautiful, functional, and uniquely yours.'}

                  <br />
                  <br />

                  By partnering with us, you gain access to our extensive
                  network of premium vendors and master craftsmen, ensuring
                  that every detail of your space is executed flawlessly.
                </p>
              </div>
            </div>
          </div>

          <hr className="my-5 opacity-25" />

          {/* Feature Section 1 */}
          <div className="row align-items-center mb-5 pb-5">
            <div className="col-lg-6 order-lg-2 mb-5 mb-lg-0 px-lg-5">
              <div className="service-detail-image wow fadeInRight">
                <figure
                  className="image-anime rounded overflow-hidden shadow-sm"
                  style={{ borderRadius: '20px' }}
                >
                  <img
                    src="/images/about-img-1.jpg"
                    alt={`${service.title} planning and design`}
                    className="img-fluid w-100"
                  />
                </figure>
              </div>
            </div>

            <div className="col-lg-6 order-lg-1 px-lg-4">
              <div className="service-detail-content wow fadeInLeft">
                <h3 className="mb-4 fw-bold display-6">
                  {service.feature1?.title ||
                    'Meticulous Planning & Sourcing'}
                </h3>

                <p
                  className="text-muted"
                  style={{ lineHeight: '1.8', fontSize: '1.05rem' }}
                >
                  {service.feature1?.description ||
                    'Great design begins with a solid foundation of planning. We leave no stone unturned when evaluating the space, drafting layouts, and determining flow. Our sourcing process involves curating only the highest quality materials, textiles, and fixtures, handpicked to ensure they not only look stunning but endure the test of time.'}
                </p>

                <ul className="list-unstyled text-muted mt-4">
                  {(
                    service.feature1?.list || [
                      'Thorough Spatial Analysis',
                      'Exclusive Material Sourcing',
                      'Custom Furniture Selection'
                    ]
                  ).map((item, idx) => (
                    <li
                      key={idx}
                      className="mb-3 d-flex align-items-center"
                    >
                      <div
                        className="bg-light rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <i
                          className="fa-solid fa-check"
                          style={{ color: '#95bad6', fontSize: '14px' }}
                        />
                      </div>

                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Feature Section 2 */}
          <div className="row align-items-center mb-5 pb-5">
            <div className="col-lg-6 mb-5 mb-lg-0 px-lg-5">
              <div className="service-detail-image wow fadeInLeft">
                <figure
                  className="image-anime rounded overflow-hidden shadow-sm"
                  style={{ borderRadius: '20px' }}
                >
                  <img
                    src="/images/service-entry-img.jpg"
                    alt={`${service.title} execution and finishing`}
                    className="img-fluid w-100"
                  />
                </figure>
              </div>
            </div>

            <div className="col-lg-6 px-lg-4">
              <div className="service-detail-content wow fadeInRight">
                <h3 className="mb-4 fw-bold display-6">
                  {service.feature2?.title ||
                    'Expert Execution & Finishing'}
                </h3>

                <p
                  className="text-muted"
                  style={{ lineHeight: '1.8', fontSize: '1.05rem' }}
                >
                  {service.feature2?.description ||
                    "The transition from concept to reality is where the magic happens. We manage every phase of the installation with rigorous attention to detail. Whether it's overseeing structural enhancements or carefully placing the final accent pieces, our objective is absolute perfection and client satisfaction."}
                </p>

                <ul className="list-unstyled text-muted mt-4">
                  {(
                    service.feature2?.list || [
                      'On-site Project Management',
                      'Professional Installation',
                      'Pre-Handover Quality Checks'
                    ]
                  ).map((item, idx) => (
                    <li
                      key={idx}
                      className="mb-3 d-flex align-items-center"
                    >
                      <div
                        className="bg-light rounded-circle p-2 me-3 d-flex align-items-center justify-content-center"
                        style={{ width: '32px', height: '32px' }}
                      >
                        <i
                          className="fa-solid fa-check"
                          style={{ color: '#95bad6', fontSize: '14px' }}
                        />
                      </div>

                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <hr className="my-5 opacity-25" />

          {/* Process Section */}
          <div className="row mt-5 pt-4 mb-5 pb-5">
            <div className="col-lg-8 mx-auto text-center wow fadeInUp">
              <span
                className="text-uppercase overview-pill text-dark"
                style={{ backgroundColor: '#f8f9fa' }}
              >
                Methodology
              </span>

              <h3 className="mb-5 mt-3 display-5 fw-bold text-anime-style-2">
                Our Process
              </h3>
            </div>

            <div className="row g-4">
              {detailedInfo.map((text, idx) => (
                <div className="col-md-6 col-lg-3" key={idx}>
                  <div
                    className="card h-100 process-card shadow-sm wow fadeInUp"
                    data-wow-delay={`${idx * 0.15}s`}
                  >
                    <div className="card-body p-5 text-center">
                      <div
                        className="mb-4 mx-auto d-flex align-items-center justify-content-center text-white process-icon shadow-sm"
                        style={{
                          width: '70px',
                          height: '70px',
                          borderRadius: '50%',
                          backgroundColor: '#1c1c1c'
                        }}
                      >
                        <h3 className="m-0 text-white fw-light">
                          {`0${idx + 1}`}
                        </h3>
                      </div>

                      <p
                        className="card-text text-secondary mt-3"
                        style={{ lineHeight: '1.7' }}
                      >
                        {text}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Project Gallery */}
      <div
        className="service-gallery-section py-5"
        style={{ backgroundColor: '#fafafa' }}
      >
        <div className="container py-5">
          <div className="row mb-5">
            <div className="col-lg-8 mx-auto text-center wow fadeInUp">
              <span className="text-uppercase overview-pill">
                Portfolio
              </span>

              <h3 className="mt-3 mb-4 display-5 fw-bold text-anime-style-2">
                Related {service.title} Projects
              </h3>

              <p
                className="text-muted fs-5 mx-auto"
                style={{ maxWidth: '700px' }}
              >
                Take a look at some of our recent transformations and be
                inspired by what we can create for you.
              </p>
            </div>
          </div>

          <div className="row g-4 page-gallery-box">
            {galleryImages.map((imgSrc, index) => (
              <div className="col-lg-4 col-md-6 col-12" key={index}>
                <div
                  className="photo-gallery wow fadeInUp"
                  data-wow-delay={`${(index % 3) * 0.15}s`}
                  data-cursor-text="Expand"
                >
                  <a
                    href="#!"
                    onClick={(e) => {
                      e.preventDefault();
                      openLightbox(index);
                    }}
                    aria-label={`View ${currentSeo.imageAlt}, image ${index + 1}`}
                  >
                    <figure
                      className="image-anime protect-wrap shadow-sm pt-0 m-0 gallery-img-wrap"
                      style={{ borderRadius: '15px' }}
                    >
                      <img
                        src={imgSrc}
                        alt={`${currentSeo.imageAlt} - Gallery ${index + 1}`}
                        draggable={false}
                        className="img-fluid w-100 protect-img"
                        style={{
                          height: '320px',
                          objectFit: 'cover'
                        }}
                        onContextMenu={(e) => e.preventDefault()}
                        onDragStart={(e) => e.preventDefault()}
                      />

                      <div
                        className="image-protect-overlay"
                        role="presentation"
                        aria-hidden="true"
                        onContextMenu={(e) => e.preventDefault()}
                        onMouseDown={(e) => e.preventDefault()}
                        onTouchStart={(e) => e.preventDefault()}
                      />
                    </figure>
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="container my-5 py-5 text-center">
        <div className="row justify-content-center wow fadeInUp">
          <div className="col-lg-10">
            <div className="p-5 p-md-5 rounded-4 premium-cta shadow-lg">
              <h2 className="mb-4 display-5 fw-bold text-white">
                Ready to Start Your {service.title} Project?
              </h2>

              <p
                className="mb-5 mx-auto opacity-75 fs-5"
                style={{ maxWidth: '700px' }}
              >
                Get in touch with us today and let's craft a beautiful,
                functional space that perfectly aligns with your vision.
              </p>

              <Link
                to="/contact-us"
                className="btn premium-btn px-5 py-3 rounded-pill text-uppercase fs-6 shadow-sm"
                data-cursor-text="Contact"
              >
                Book a Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <Lightbox
          images={galleryImages}
          startIndex={startIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </>
  );
};

export default ServiceDetail;
